import { useMemo } from 'react';
import urlRegex from 'url-regex';

export type ShortenedUrlDetectionResult = {
  hasShortenedUrl: boolean;
  detectedDomain: string | null;
  offendingUrl: string | null;
  offendingUrlIndex: number | null;
};

/**
 * Extracts the hostname from a URL string.
 * Handles both fully-qualified URLs (https://youtu.be/abc) and
 * scheme-less bare links (youtu.be/abc)
 */
function extractHostname(url: string): string {
  try {
    return new URL(url).hostname.toLowerCase();
  } catch {
    try {
      return new URL(`https://${url}`).hostname.toLowerCase();
    } catch {
      return '';
    }
  }
}

/**
 * Pure function: detects whether `body` contains a URL whose hostname is in `blockedDomains`.
 * Returns the first match. Comparison is case-insensitive.
 *
 * Exported so callers can run a synchronous check (e.g. at send-time on the live body value)
 * without waiting for the debounce cycle to complete.
 */
export function detectShortenedUrl(body: string, blockedDomains: string[]): ShortenedUrlDetectionResult {
  const empty: ShortenedUrlDetectionResult = {
    hasShortenedUrl: false,
    detectedDomain: null,
    offendingUrl: null,
    offendingUrlIndex: null,
  };

  if (!body || !blockedDomains.length) return empty;

  const blockedDomainSet = new Set(blockedDomains.map((d) => d.toLowerCase()));

  // url-regex({ strict: false }) matches bare hostnames like youtu.be/abc without https://
  const regex = urlRegex({ strict: false });
  let match: RegExpExecArray | null;

  while ((match = regex.exec(body)) !== null) {
    const urlStr = match[0];
    const hostname = extractHostname(urlStr);
    if (hostname && blockedDomainSet.has(hostname)) {
      return {
        hasShortenedUrl: true,
        detectedDomain: hostname,
        offendingUrl: urlStr,
        offendingUrlIndex: match.index,
      };
    }
  }

  return empty;
}

/**
 * Detects whether `debouncedBody` contains a URL whose hostname is in `blockedDomains`.
 * Returns the first match. Comparison is case-insensitive (addresses backend bug B-1).
 *
 * Accepts `debouncedBody` (already debounced by the caller) so no additional debounce overhead
 * is introduced.
 */
export const useShortenedUrlDetection = (
  debouncedBody: string,
  blockedDomains: string[]
): ShortenedUrlDetectionResult => {
  return useMemo<ShortenedUrlDetectionResult>(() => {
    return detectShortenedUrl(debouncedBody, blockedDomains);
  }, [debouncedBody, blockedDomains]);
};
